import { useMemo, useState } from "react";

import { FileUpload as FileUploadIcon } from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { postItemTypeOption } from "@rubi/api-generated/clientRs/sdk.gen";
import { toast } from "@rubi/ui";

import { parseItemListJson } from "./parseItemListJson";

interface ImportDropdownJsonModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onSuccess: () => void;
}

const ImportDropdownJsonModal = ({
  open,
  onClose,
  orgId,
  onSuccess,
}: ImportDropdownJsonModalProps) => {
  const [title, setTitle] = useState("");
  const [json, setJson] = useState("");
  const [importing, setImporting] = useState(false);

  const parsed = useMemo(
    () => (json.trim() ? parseItemListJson(json) : null),
    [json],
  );
  const canImport = !!title.trim() && parsed?.ok === true && !importing;

  const resetAndClose = () => {
    if (importing) return;
    setTitle("");
    setJson("");
    onClose();
  };

  const handleImport = async () => {
    if (!parsed?.ok || !title.trim()) return;
    try {
      setImporting(true);
      await postItemTypeOption({
        body: {
          rubi_org_id: orgId,
          name: title.trim(),
          items: parsed.itemList,
        },
      });
      toast.success(
        `Imported "${title.trim()}" with ${parsed.itemList.items.length} items`,
      );
      setImporting(false);
      resetAndClose();
      onSuccess();
    } catch (err: any) {
      console.error("Failed to import dropdown list:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to import list",
      );
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Dropdown List from JSON</DialogTitle>
      <DialogContent>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          margin="normal"
          disabled={importing}
        />
        <TextField
          label="Version 2 JSON"
          placeholder={
            '{\n  "version": 2,\n  "items": [\n    { "label": "Yes", "value": "Y" }\n  ]\n}'
          }
          value={json}
          onChange={(e) => setJson(e.target.value)}
          fullWidth
          required
          multiline
          minRows={10}
          maxRows={20}
          margin="normal"
          disabled={importing}
          slotProps={{
            input: { sx: { fontFamily: "monospace", fontSize: 13 } },
          }}
        />
        {parsed &&
          (parsed.ok ? (
            <Alert severity="success">
              {parsed.itemList.items.length} items ready to import.
            </Alert>
          ) : (
            <Alert severity="error">{parsed.error}</Alert>
          ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!canImport}
          startIcon={
            importing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <FileUploadIcon />
            )
          }
        >
          {importing ? "Importing..." : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDropdownJsonModal;
